import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";

import useGetVisitAdvancedSurveys from "../../hooks/useGetVisitAdvancedSurveys";
import useGetAdvancedSurveys from "../../hooks/useGetAdvancedSurveys";
import useUpdateVisitAdvancedSurveysMutation from "../../hooks/useUpdateVisitAdvancedSurveysMutation";
import useGetPhotoSessionAdvancedSurveys from "../../hooks/useGetPhotoSessionAdvancedSurveys";
import useUpdatePhotoSessionAdvancedSurveysMutation from "../../hooks/useUpdatePhotoSessionAdvancedSurveysMutation";
import CustomButton from "../UI/CustomButton";
import SaveForFollowingVisitsCheckbox, {
  canSaveForFollowingVisits,
} from "./SaveForFollowingVisitsCheckbox";
import { USER_ROLES, INV_RELATED_ROLES } from "../../utils/constants";

const AdvancedSurveysModule = ({
  role,
  selectedSessionId,
  selectedVisit,
  visitId,
  patientId,
  step,
  headerless = false,
  manageTestId,
  isTrialLocked = false,
}) => {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);

  // CRO's ordered survey list (used in CRO mode)
  const [selectedSurveys, setSelectedSurveys] = useState([]);

  // Investigator's checked survey IDs (used in investigator mode)
  const [invSelectedIds, setInvSelectedIds] = useState([]);

  const [localDisallowInv, setLocalDisallowInv] = useState(false);
  const [localIsAllSurvey, setLocalIsAllSurvey] = useState(false);

  const isInvestigatorRelated = INV_RELATED_ROLES.includes(role);

  const sessionId = selectedSessionId || selectedVisit?.photo_session_id;
  const stepId = step?.id;
  const label = step?.title || "ePRO / POEMs / eDiary";
  const testIdSuffix = stepId ? `-${stepId}` : "";

  // CRO superset — returns { surveys, disallow_inv_survey_selection }
  const { data: visitSurveysData = {} } = useGetVisitAdvancedSurveys({
    visitId,
    stepId,
  });
  const visitSurveys = visitSurveysData.surveys || [];

  const isReadOnly =
    isTrialLocked ||
    role === USER_ROLES.REVIEWER ||
    (patientId && visitSurveysData?.disallow_inv_survey_selection);

  // Adverse Event and eDiary visits must always keep the investigator locked out of picking
  // surveys — CRO can't turn this off.
  const isAdverseEventVisit = selectedVisit?.visit_number === -1;
  const isEdiaryVisit = selectedVisit?.visit_number === -6;
  const isInvestigatorLockedVisit = isAdverseEventVisit || isEdiaryVisit;

  // All available surveys — for CRO to pick from when dialog is open
  const { data: allSurveys } = useGetAdvancedSurveys({
    paginate: false,
    enabled: open && !patientId && role === USER_ROLES.CRO,
  });

  // Session-level surveys — always fetched in patient mode to drive the enabled checkbox
  const { data: sessionSurveysData } = useGetPhotoSessionAdvancedSurveys({
    sessionId,
    stepId,
    enabled: !!patientId,
  });

  const updateMutation = useUpdateVisitAdvancedSurveysMutation();
  const sessionMutation = useUpdatePhotoSessionAdvancedSurveysMutation();

  // CRO mode: sync selected surveys + enabled from visit data
  useEffect(() => {
    if (!patientId && visitSurveys?.length) {
      setEnabled(true);
      const sorted = [...visitSurveys].sort((a, b) => a.order_number - b.order_number);
      setSelectedSurveys(
        sorted.map((s) => ({
          survey_id: s.id,
          name: s.name,
          order_number: s.order_number,
        }))
      );
    }
  }, [visitSurveys, patientId]);

  // Patient mode: enabled only when the investigator has explicitly assigned surveys to this
  // session — surveys merely inherited from the visit are not a real choice yet. Exception:
  // when the investigator is locked out of picking (disallow_inv), there will never be an
  // explicit session selection, so the visit's forced superset counts as "on" instead.
  useEffect(() => {
    if (
      patientId &&
      (sessionSurveysData?.is_session_selection ||
        visitSurveysData?.disallow_inv_survey_selection) &&
      sessionSurveysData?.surveys?.length
    ) {
      setEnabled(true);
    }
  }, [patientId, sessionSurveysData, visitSurveysData]);

  // Initialise local state from server data each time the dialog opens
  useEffect(() => {
    if (open) {
      setLocalDisallowInv(
        isInvestigatorLockedVisit || !!visitSurveysData?.disallow_inv_survey_selection
      );
      setLocalIsAllSurvey(!!visitSurveysData?.is_all_survey);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate investigator's selection when dialog opens — only when the session has an
  // explicit override; otherwise the visit's surveys are just inherited, not actually chosen yet.
  // Exception: when disallow_inv is set, the visit's forced superset is shown (read-only) instead.
  useEffect(() => {
    if (
      open &&
      patientId &&
      (sessionSurveysData?.is_session_selection ||
        visitSurveysData?.disallow_inv_survey_selection) &&
      sessionSurveysData?.surveys
    ) {
      setInvSelectedIds(sessionSurveysData.surveys.map((s) => s.id));
    } else if (open && patientId) {
      setInvSelectedIds([]);
    }
  }, [open, sessionSurveysData, patientId, visitSurveysData]);

  const selectedIds = useMemo(() => selectedSurveys.map((s) => s.survey_id), [selectedSurveys]);

  const unselectedSurveys = useMemo(
    () => (allSurveys || []).filter((s) => !selectedIds.includes(s.id)),
    [allSurveys, selectedIds]
  );

  const allCROSelected =
    (allSurveys || []).length > 0 && selectedSurveys.length === (allSurveys || []).length;

  const allInvSelected = visitSurveys.length > 0 && invSelectedIds.length === visitSurveys.length;

  // ── CRO mode handlers ──────────────────────────────────────────────────────

  const moveItem = (index, direction) => {
    setSelectedSurveys((prev) => {
      const newOrder = [...prev];
      const target = index + direction;
      if (target < 0 || target >= newOrder.length) return prev;

      const temp = newOrder[index];
      newOrder[index] = newOrder[target];
      newOrder[target] = temp;

      return newOrder.map((s, i) => ({ ...s, order_number: i + 1 }));
    });
  };

  const toggleSurvey = (survey) => {
    setSelectedSurveys((prev) => {
      const exists = prev.find((s) => s.survey_id === survey.id);
      if (exists) {
        return prev
          .filter((s) => s.survey_id !== survey.id)
          .map((s, i) => ({ ...s, order_number: i + 1 }));
      }
      return [
        ...prev,
        {
          survey_id: survey.id,
          name: survey.name,
          order_number: prev.length + 1,
        },
      ];
    });
  };

  const handleSelectAllCRO = () => {
    if (allCROSelected) {
      setSelectedSurveys([]);
    } else {
      setSelectedSurveys(
        (allSurveys || []).map((s, i) => ({
          survey_id: s.id,
          name: s.name,
          order_number: i + 1,
        }))
      );
    }
  };

  const handleToggleEnabled = (e) => {
    const checked = e.target.checked;
    setEnabled(checked);
    if (!checked) {
      if (patientId) {
        // Investigator/CRC: clear their session-level selection
        setInvSelectedIds([]);
        if (sessionId) {
          sessionMutation.mutate({ sessionId, stepId, surveys: [] });
        }
      } else {
        // CRO: clear the visit-level superset
        setSelectedSurveys([]);
        updateMutation.mutate({ visitId, stepId, surveys: [] });
      }
    }
  };

  const handleDisableInvSelection = () => {
    setLocalDisallowInv((prev) => !prev);
  };

  // Investigator mode handlers
  const toggleInvSurvey = (id) => {
    setInvSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAllInv = () => {
    setInvSelectedIds(allInvSelected ? [] : visitSurveys.map((s) => s.id));
  };

  // Save
  const handleSave = () => {
    if (patientId && !isReadOnly) {
      // Investigator saves session-level subset
      const orderedSurveys = visitSurveys
        .filter((s) => invSelectedIds.includes(s.id))
        .map((s, i) => ({ survey_id: s.id, order_number: i + 1 }));
      sessionMutation.mutate(
        { sessionId, stepId, surveys: orderedSurveys },
        { onSuccess: () => setOpen(false) }
      );
    } else {
      // CRO saves visit-level superset + disallow flag
      updateMutation.mutate(
        {
          visitId,
          stepId,
          surveys: selectedSurveys.map(({ survey_id, order_number }) => ({
            survey_id,
            order_number,
          })),
          disallowInvSurveySelection: headerless ? false : localDisallowInv,
          isAllSurvey: headerless ? undefined : localIsAllSurvey,
        },
        { onSuccess: () => setOpen(false) }
      );
    }
  };

  // Determine who can manage
  const canManage = patientId
    ? !isReadOnly &&
      (isInvestigatorRelated || role === USER_ROLES.CRO || role === USER_ROLES.SUPER_ADMIN)
    : role === USER_ROLES.CRO;

  const isCroRelatedRole =
    role === USER_ROLES.CRO ||
    role === USER_ROLES.CRO_COORDINATOR ||
    role === USER_ROLES.DATA_MANAGER ||
    role === USER_ROLES.SUPER_ADMIN;

  // Investigators are not shown modules the CRO did not configure for this visit
  if (
    isInvestigatorRelated &&
    patientId &&
    !headerless &&
    selectedVisit?.visit_number >= 0 &&
    visitSurveysData?.visit_configured === false
  ) {
    return null;
  }

  return (
    <Box sx={headerless ? {} : { width: "100%" }}>
      {headerless ? (
        <CustomButton
          size="small"
          variant="outlined"
          onClick={() => setOpen(true)}
          data-testid={manageTestId}
        >
          {canManage ? "Manage" : "View"}
        </CustomButton>
      ) : (
        /* Header */
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox
              checked={enabled}
              onChange={handleToggleEnabled}
              disabled={
                patientId
                  ? isReadOnly || !visitSurveys.length || sessionMutation.isPending
                  : role !== USER_ROLES.CRO || updateMutation.isPending
              }
              inputProps={{ "data-testid": `adv-surveys-enable-checkbox-input${testIdSuffix}` }}
            />
            <Typography variant="subtitle1" fontWeight={600}>
              {label}
            </Typography>
          </Stack>

          {enabled && (
            <Stack direction="row" spacing={1} alignItems="center">
              {patientId && (
                <Tooltip title="Go to ePRO">
                  <IconButton
                    component={Link}
                    target="_blank"
                    to={`/patient/${patientId}/advanced-surveys/${!!selectedSessionId ? selectedSessionId : selectedVisit?.photo_session_id}/?visitName=${encodeURIComponent(selectedVisit?.visit_name || "")}`}
                    size="small"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <CustomButton
                size="small"
                data-testid={`adv-surveys-manage-btn${testIdSuffix}`}
                onClick={() => setOpen(true)}
              >
                {canManage ? "Manage" : "View"}
              </CustomButton>
            </Stack>
          )}
        </Box>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {canManage ? "Manage" : "View"} {label}
        </DialogTitle>

        <DialogContent dividers>
          {!headerless && !patientId && !isReadOnly && isCroRelatedRole && (
            <Box sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  checked={localDisallowInv}
                  onChange={handleDisableInvSelection}
                  disabled={isInvestigatorLockedVisit}
                />
                <Typography variant="body2">
                  Disable survey selection for investigator
                  {isAdverseEventVisit && " (always enabled for Adverse Event visits)"}
                  {isEdiaryVisit && " (always enabled for eDiary visits)"}
                </Typography>
              </Stack>

              {canSaveForFollowingVisits(selectedVisit) && (
                <SaveForFollowingVisitsCheckbox
                  label="Save ePRO configurations for all coming visits"
                  checked={localIsAllSurvey}
                  onChange={() => setLocalIsAllSurvey((prev) => !prev)}
                />
              )}
            </Box>
          )}

          {/* ── CRO mode: ordered selection ── */}
          {!patientId && (
            <>
              {/* Select / Deselect All */}
              {role === USER_ROLES.CRO && (
                <Typography
                  variant="body2"
                  sx={{ cursor: "pointer", color: "primary.main", mb: 1 }}
                  onClick={handleSelectAllCRO}
                >
                  {allCROSelected ? "Deselect All ePRO: " : "Select All ePRO: "}
                  {selectedSurveys.length}
                </Typography>
              )}

              <List>
                {selectedSurveys.map((survey, index) => (
                  <ListItem key={survey.survey_id} disableGutters sx={{ mb: 1 }}>
                    <Paper
                      sx={{
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Checkbox
                        checked
                        onChange={() => toggleSurvey({ id: survey.survey_id })}
                        disabled={role !== USER_ROLES.CRO}
                      />
                      <ListItemText
                        primary={survey.name}
                        secondary={`Order ${survey.order_number}`}
                      />
                      <Stack direction="row">
                        <IconButton
                          size="small"
                          onClick={() => moveItem(index, -1)}
                          disabled={index === 0 || role !== USER_ROLES.CRO}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveItem(index, 1)}
                          disabled={index === selectedSurveys.length - 1 || role !== USER_ROLES.CRO}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  </ListItem>
                ))}

                {unselectedSurveys.map((survey) => (
                  <ListItem key={survey.id} disableGutters sx={{ mb: 1 }}>
                    <Paper
                      sx={{
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Checkbox checked={false} onChange={() => toggleSurvey(survey)} />
                      <ListItemText primary={survey.name} />
                    </Paper>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {/* ── Patient / investigator mode: subset selection from CRO superset ── */}
          {patientId && (
            <>
              {/* Select / Deselect All */}
              {!isReadOnly && (
                <Typography
                  variant="body2"
                  sx={{ cursor: "pointer", color: "primary.main", mb: 1 }}
                  onClick={handleSelectAllInv}
                >
                  {allInvSelected ? "Deselect All ePRO: " : "Select All ePRO: "}
                  {invSelectedIds.length}
                </Typography>
              )}

              <List>
                {visitSurveys.map((survey) => (
                  <ListItem key={survey.id} disableGutters sx={{ mb: 1 }}>
                    <Paper
                      sx={{
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Checkbox
                        checked={invSelectedIds.includes(survey.id)}
                        disabled={isReadOnly}
                        onChange={() => toggleInvSurvey(survey.id)}
                      />
                      <ListItemText primary={survey.name} />
                    </Paper>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canManage || updateMutation.isPending || sessionMutation.isPending}
          >
            {updateMutation.isPending || sessionMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvancedSurveysModule;
